import { BehaviorSubject, Subject, catchError, filter, map, of, share, switchMap, tap, throwError } from 'rxjs';
import { CrudRequestEventType } from '@core/shared/crud';
/** global item event bus allowing to react to item modifications from anywhere
 * NOTE: CrudRequestState doesn't know anything about the payload! It only knows about the Model and affected ids!
*/
export class CrudRequestStateShared {
  constructor() {
    this.paused$ = new BehaviorSubject(false);
    this.events$ = new Subject();
    this.pendingRequests$ = new BehaviorSubject([]);
    this.debugModels = [];
  }
  listenTo(model, eventType) {
    return this.events$.pipe(filter(e => e.model === model && (!eventType || e.eventType === eventType)), filter(e => !this.paused$.value));
  }
  pauseEvents(enablePaused) {
    this.paused$.next(enablePaused);
  }
  debugModel(model) {
    this.debugModels.push(model);
  }
  getPendingRequests$(model) {
    return this.pendingRequests$.pipe(map(requests => {
      return requests.filter(pendingRequest => pendingRequest.model === model);
    }));
  }
  getLoadingState$(model) {
    return this.getPendingRequests$(model).pipe(map(pending => {
      let allLoading = false;
      const idsLoading = [];
      pending.forEach(pendingRequestInfo => {
        if (pendingRequestInfo.ids) {
          idsLoading.push(...pendingRequestInfo.ids);
        } else {
          allLoading = true;
        }
      });
      return {
        allLoading,
        anyLoading: allLoading || idsLoading.length > 0,
        ids: idsLoading.length ? idsLoading : null
      };
    }));
  }
  bindRequest(model, request, hot, type, info = {}) {
    const removeFromPending = (requestToRemove, error = false) => {
      const pending = [...this.pendingRequests$.getValue()];
      const index = pending.findIndex(r => r.request === requestToRemove);
      pending.splice(index, 1);
      this.pendingRequests$.next(pending);
    };
    if (!IS_PRODUCTION && this.debugModels.includes(model)) console.warn('bindRequest');
    const newRequestInfo = {
      model,
      request,
      type,
      ids: info.ids || null
    };
    const boundRequest = of(null).pipe(tap(() => {
      // add to pending Requests queue
      const existingPendingRequests = this.pendingRequests$.getValue();
      this.pendingRequests$.next([...existingPendingRequests, newRequestInfo]);
    }), switchMap(() => {
      // switch to actual request 
      if (!IS_PRODUCTION && this.debugModels.includes(model)) console.log('>>> switch to request', model, type);
      return request;
    }),
    // bind events + auto-removal from pending list
    tap(response => {
      if (!IS_PRODUCTION && this.debugModels.includes(model)) console.log('>>> emit RequestCompleted event');
      this.events$.next({
        model,
        crudAction: type,
        eventType: CrudRequestEventType.RequestCompleted,
        requestInfo: newRequestInfo,
        response
      });
      removeFromPending(request);
    }), catchError(error => {
      this.events$.next({
        model,
        crudAction: type,
        eventType: CrudRequestEventType.RequestFailed,
        requestInfo: newRequestInfo,
        error
      });
      removeFromPending(request, error);
      return throwError(() => error);
    }), share());
    if (!hot) {
      return boundRequest;
    } else {
      boundRequest.subscribe();
      return boundRequest;
    }
  }
}